import { isDefined } from "@clipboard-health/util-ts";
import { formatTimeWithTimezone } from "@src/appV2/redesign/utils/formatTimeWithTimezone";
import { addHours, endOfMinute, isAfter, isBefore, startOfMinute } from "date-fns";

import { MAX_AFTER_SHIFT_END_THRESHOLD_HOURS } from "../../Timekeeping/Timesheet/utils/constants";

export interface ValidateClockOutTimestampParams {
  selectedTime: Date;
  /**
   * Reference time used for "in the future" validation. Defaults to current time.
   * Primarily provided for deterministic testing.
   */
  currentTime?: Date;
  clockInTime: Date;
  geofenceDepartureTime?: Date;
  latestPlausibleDepartureTime?: Date;
  shiftEndAt: Date;
  workerClockOut?: Date;
  isGeofenceFacility?: boolean;
  isNfcOrMeshFacility?: boolean;
  isDirectIntegrationFacility?: boolean;
  isEnforceGeofenceTimestampsEnabled?: boolean;
  preventLateClockOut?: boolean;
  verifiedClockOut?: Date;
  workplaceTimezone: string;
}

export type ClockOutErrorType =
  | "TIMESTAMPS_NOT_IN_ORDER"
  | "GEOFENCE_ERROR"
  | "TOO_LATE_CLOCK_OUT"
  | "AFTER_SHIFT_END"
  | "IN_FUTURE"
  | "REDUCES_VERIFIED_TIME";

export type ClockOutWarningType = "MISSED_PUNCH_WARNING";

export type ClockOutGeofenceValidationType = "exact" | "plausible";

export interface ClockOutValidationResult {
  isValid: boolean;
  errorType?: ClockOutErrorType;
  warningType?: ClockOutWarningType;
  message?: string;
  geofenceValidationType?: ClockOutGeofenceValidationType;
}

/**
 * Validates clock out timestamp against business rules
 *
 * Returns error states that block submission or warning states that require
 * a reason but allow submission.
 *
 * Error Priority Order:
 * 1. Timestamps not in order (clock in after clock out)
 * 2. Geofence error
 * 3. After shift end (when late clock out is not allowed)
 * 4. Too late (>6 hours after shift end)
 *
 * Warning Conditions:
 * - Missed punch warning (blue banner)
 */
export function validateClockOutTimestamp(
  params: ValidateClockOutTimestampParams
): ClockOutValidationResult {
  const {
    selectedTime,
    currentTime,
    clockInTime,
    geofenceDepartureTime,
    latestPlausibleDepartureTime,
    shiftEndAt,
    workerClockOut,
    isGeofenceFacility = false,
    isNfcOrMeshFacility = false,
    isDirectIntegrationFacility = false,
    isEnforceGeofenceTimestampsEnabled = false,
    preventLateClockOut = false,
    verifiedClockOut,
    workplaceTimezone,
  } = params;

  const now = currentTime ?? new Date();

  // Priority 0: Time in the future
  const futureTimeError = checkFutureTimeError(selectedTime, workplaceTimezone, now);
  if (futureTimeError) {
    return futureTimeError;
  }

  // Priority 1: Verified time reduction error
  const verifiedTimeError = checkVerifiedTimeReductionError(selectedTime, verifiedClockOut);
  if (verifiedTimeError) {
    return verifiedTimeError;
  }

  // Priority 2: Timestamps not in order
  const timestampsNotInOrderError = checkTimestampsNotInOrderError(selectedTime, clockInTime);
  if (timestampsNotInOrderError) {
    return timestampsNotInOrderError;
  }

  // Priority 3: Geofence error (departure timestamp or Latest Plausible Departure Timestamp)
  const geofenceError = checkGeofenceError({
    selectedTime,
    workplaceTimezone,
    geofenceDepartureTime,
    latestPlausibleDepartureTime,
    isEnforceGeofenceTimestampsEnabled,
    verifiedClockOut,
  });
  if (geofenceError) {
    return geofenceError;
  }

  // Priority 4: After shift end (when late clock out is not allowed)
  if (preventLateClockOut) {
    const afterShiftEndError = checkAfterShiftEndError(selectedTime, shiftEndAt, workplaceTimezone);
    if (afterShiftEndError) {
      return afterShiftEndError;
    }
  }

  // Priority 5: Too late clock out
  const tooLateError = checkTooLateError(selectedTime, shiftEndAt);
  if (tooLateError) {
    return tooLateError;
  }

  // Check for missed punch warning (blue warning, not blocking)
  const missedPunchWarning = checkMissedPunchWarning({
    selectedTime,
    workerClockOut,
    isGeofenceFacility,
    isNfcOrMeshFacility,
    isDirectIntegrationFacility,
  });
  if (missedPunchWarning) {
    return missedPunchWarning;
  }

  // No errors or warnings - validation passed
  return {
    isValid: true,
  };
}

/**
 * Error 1: Selected time is in the future
 */
function checkFutureTimeError(
  selectedTime: Date,
  workplaceTimezone: string,
  now: Date
): ClockOutValidationResult | undefined {
  const selectedTruncated = startOfMinute(selectedTime);
  const nowTruncated = startOfMinute(now);

  if (isAfter(selectedTruncated, nowTruncated)) {
    return {
      isValid: false,
      errorType: "IN_FUTURE",
      message: `This time (${formatTimeWithTimezone(
        selectedTime,
        workplaceTimezone,
        false
      )}) is in the future. Please end your shift before requesting shift payment.`,
    };
  }

  return undefined;
}

/**
 * Error 2: Selected time is before verified clock out (reduces verified time)
 */
function checkVerifiedTimeReductionError(
  selectedTime: Date,
  verifiedClockOut: Date | undefined
): ClockOutValidationResult | undefined {
  if (!isDefined(verifiedClockOut)) {
    return undefined;
  }

  // Compare at minute granularity to match what users see in the UI
  const selectedTruncated = startOfMinute(selectedTime);
  const verifiedTruncated = startOfMinute(verifiedClockOut);

  if (isBefore(selectedTruncated, verifiedTruncated)) {
    return {
      isValid: false,
      errorType: "REDUCES_VERIFIED_TIME",
      message: "Clock out time cannot be set earlier than the verified clock out time.",
    };
  }

  return undefined;
}

/**
 * Error 2: Clock in time is after selected clock out time
 */
function checkTimestampsNotInOrderError(
  selectedTime: Date,
  clockInTime: Date
): ClockOutValidationResult | undefined {
  // Clock out must be after clock in
  if (isBefore(selectedTime, clockInTime) || selectedTime.getTime() === clockInTime.getTime()) {
    return {
      isValid: false,
      errorType: "TIMESTAMPS_NOT_IN_ORDER",
      message: "Clock out time must be after clock in time.",
    };
  }

  return undefined;
}

/**
 * Error 3: Selected time is after geofence departure
 */
function checkGeofenceError(params: {
  selectedTime: Date;
  workplaceTimezone: string;
  geofenceDepartureTime?: Date;
  latestPlausibleDepartureTime?: Date;
  isEnforceGeofenceTimestampsEnabled: boolean;
  verifiedClockOut?: Date;
}): ClockOutValidationResult | undefined {
  const {
    selectedTime,
    workplaceTimezone,
    geofenceDepartureTime,
    latestPlausibleDepartureTime,
    isEnforceGeofenceTimestampsEnabled,
    verifiedClockOut,
  } = params;

  const geofenceValidationType = isEnforceGeofenceTimestampsEnabled ? "plausible" : "exact";
  const geofenceTimestamp =
    geofenceValidationType === "exact" ? geofenceDepartureTime : latestPlausibleDepartureTime;

  // If verified clock out is later than recorded departure, geofence is unreliable
  const isGeofenceReliable = !(
    isDefined(verifiedClockOut) &&
    isDefined(geofenceTimestamp) &&
    isAfter(verifiedClockOut, endOfMinute(geofenceTimestamp))
  );

  if (isGeofenceReliable && isDefined(geofenceTimestamp)) {
    // Compare at minute granularity to align with what users see in the UI.
    // This allows selecting the same displayed minute as the recorded departure
    // without triggering a geofence error due to second-level differences.
    const selectedTruncated = startOfMinute(selectedTime);
    const departureTruncated = startOfMinute(geofenceTimestamp);

    // Selected time must not be after departure boundary
    if (isAfter(selectedTruncated, departureTruncated)) {
      return {
        isValid: false,
        errorType: "GEOFENCE_ERROR",
        message: `Clock out time cannot be after you left the facility at ${formatTimeWithTimezone(
          geofenceTimestamp,
          workplaceTimezone,
          false
        )}. Please select an earlier time.`,
        geofenceValidationType,
      };
    }
  }

  return undefined;
}

/**
 * Error 4: More than 6 hours after scheduled shift end
 */
function checkTooLateError(
  selectedTime: Date,
  shiftEndAt: Date
): ClockOutValidationResult | undefined {
  const tooLateThreshold = addHours(shiftEndAt, MAX_AFTER_SHIFT_END_THRESHOLD_HOURS);

  if (isAfter(selectedTime, tooLateThreshold)) {
    return {
      isValid: false,
      errorType: "TOO_LATE_CLOCK_OUT",
      message: `Clock out time cannot be more than ${MAX_AFTER_SHIFT_END_THRESHOLD_HOURS} hours after scheduled shift end.`,
    };
  }

  return undefined;
}

/**
 * Error: Clock out time is after scheduled shift end (when late clock out is not allowed)
 */
function checkAfterShiftEndError(
  selectedTime: Date,
  shiftEndAt: Date,
  workplaceTimezone: string
): ClockOutValidationResult | undefined {
  if (isAfter(startOfMinute(selectedTime), startOfMinute(shiftEndAt))) {
    return {
      isValid: false,
      errorType: "AFTER_SHIFT_END",
      message: `Clock out time cannot be later than the scheduled shift end time at ${formatTimeWithTimezone(
        shiftEndAt,
        workplaceTimezone,
        false
      )}.`,
    };
  }

  return undefined;
}

/**
 * Warning: Missed punch warning (blue banner)
 *
 * Conditions:
 * - No red error applies AND
 * - At geofence facility: workerClockOut not set OR selected time is later than workerClockOut
 * - At NFC/Mesh/direct integration facility: workerClockOut not set OR selected time is later
 */
function checkMissedPunchWarning(params: {
  selectedTime: Date;
  workerClockOut: Date | undefined;
  isGeofenceFacility: boolean;
  isNfcOrMeshFacility: boolean;
  isDirectIntegrationFacility: boolean;
}): ClockOutValidationResult | undefined {
  const {
    selectedTime,
    workerClockOut,
    isGeofenceFacility,
    isNfcOrMeshFacility,
    isDirectIntegrationFacility,
  } = params;

  // Only applies to facilities with timekeeping systems
  if (!isGeofenceFacility && !isNfcOrMeshFacility && !isDirectIntegrationFacility) {
    return undefined;
  }

  const missedPunchMessage =
    "You are requesting a later clock out time. No worries - you'll be able to add the reason on the next screen.";

  // Warning if workerClockOut is not set
  if (!isDefined(workerClockOut)) {
    return {
      isValid: true,
      warningType: "MISSED_PUNCH_WARNING",
      message: missedPunchMessage,
    };
  }

  // Warning if selected time is later than workerClockOut
  if (isAfter(selectedTime, workerClockOut)) {
    return {
      isValid: true,
      warningType: "MISSED_PUNCH_WARNING",
      message: missedPunchMessage,
    };
  }

  return undefined;
}
